// accordion.js
function getToggles(root = document) {
	return Array.from(root.querySelectorAll('.site-accordion__toggle'));
}
function getContent(toggle) {
	const id = toggle.getAttribute('aria-controls');
	if (!id) {
		return null;
	} // guard
	const el = document.getElementById(id);
	return el || null;
}
function isExpanded(toggle) {
	return toggle.getAttribute('aria-expanded') === 'true';
}
function setExpanded(toggle, expanded) {
	toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}
function setHidden(content, hidden) {
	if (hidden) {
		content.setAttribute('inert', '');
	} else {
		content.removeAttribute('inert');
	}
}
function refreshHeights(content) {
	if (!content || content._isAnimating) {
		return;
	}
	content.style.maxHeight = 'none';
	const h = content.scrollHeight;
	content.style.maxHeight = h + 'px';

	let ancestor = content.parentElement?.closest('.site-accordion__content:not([inert])');
	while (ancestor) {
		ancestor.style.maxHeight = 'none';
		ancestor.style.maxHeight = ancestor.scrollHeight + 'px';
		ancestor = ancestor.parentElement?.closest('.site-accordion__content:not([inert])');
	}
}
function expand(toggle, content) {
	setExpanded(toggle, true);
	setHidden(content, false);
	content._isAnimating = true;

	// Disable transition, measure natural height, set start value
	content.style.transition = 'none';
	content.style.maxHeight = 'none';
	const h = content.scrollHeight;
	content.style.maxHeight = '0px';
	content.offsetHeight; // force reflow to commit '0px'

	// Re-enable transition and animate to target
	content.style.transition = '';
	content.style.maxHeight = h + 'px';

	// Clear animating flag when transition completes
	content.addEventListener('transitionend', function handler() {
		content._isAnimating = false;
		content.removeEventListener('transitionend', handler);
	});

	// Bubble to any open ancestor accordions
	let ancestor = content.parentElement?.closest('.site-accordion__content:not([inert])');
	while (ancestor) {
		ancestor.style.maxHeight = 'none';
		ancestor.style.maxHeight = ancestor.scrollHeight + 'px';
		ancestor = ancestor.parentElement?.closest('.site-accordion__content:not([inert])');
	}
}
function collapse(toggle, content) {
	// Snapshot current height as a px value so the transition has a start point
	content.style.maxHeight = content.scrollHeight + 'px';
	content.offsetHeight; // force reflow
	setExpanded(toggle, false);
	setHidden(content, true);
	content.style.maxHeight = '0px';
}

// Public helper: open by id/container
export function openAccordion(selectorOrId, root = document) {
	const toggles = getToggles(root);
	const container = selectorOrId ? root.querySelector(selectorOrId) : null;

	if (container && container.classList.contains('site-accordion__content')) {
		const id = container.id;
		const t = toggles.find((btn) => btn.getAttribute('aria-controls') === id);
		if (t) {
			const c = getContent(t);
			if (c && !isExpanded(t)) {
				expand(t, c);
			}
		}
		return;
	}

	if (container) {
		const t = container.querySelector('.site-accordion__toggle');
		if (t) {
			const c = getContent(t);
			if (c && !isExpanded(t)) {
				expand(t, c);
			}
		}
		return;
	}

	if (selectorOrId && selectorOrId.startsWith('#')) {
		const id = selectorOrId.slice(1);
		const t = toggles.find((btn) => btn.getAttribute('aria-controls') === id || btn.getAttribute('aria-controls') === selectorOrId);
		if (t) {
			const c = getContent(t);
			if (c && !isExpanded(t)) {
				expand(t, c);
			}
		}
	}
}

export function initAccordions(root = document) {
	const toggles = getToggles(root);
	if (!toggles.length) {
		return;
	}

	toggles.forEach((btn) => {
		const content = getContent(btn);
		if (!content) {
			return;
		}

		// Ensure required class is present
		content.classList.add('site-accordion__content');

		// Initial state
		if (isExpanded(btn)) {
			setHidden(content, false);
			refreshHeights(content);
		} else {
			setHidden(content, true);
			content.style.maxHeight = '0px';
		}

		// Click handler (idempotent)
		if (!btn._accordionBound) {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				const expanded = isExpanded(btn);
				expanded ? collapse(btn, content) : expand(btn, content);
			});
			// Keyboard A11y: Space/Enter toggle
			btn.addEventListener('keydown', (e) => {
				if (e.key === ' ' || e.key === 'Enter') {
					e.preventDefault();
					const expanded = isExpanded(btn);
					expanded ? collapse(btn, content) : expand(btn, content);
				}
			});
			btn._accordionBound = true;
		}

		// Keep height in sync if inner content changes
		if (!content._accordionObserver) {
			const obs = new ResizeObserver(() => {
				if (!content.hasAttribute('inert')) {
					refreshHeights(content);
				}
			});
			obs.observe(content);
			content._accordionObserver = obs;
		}
	});

	// Recompute heights on viewport resize (once per root)
	const openContents = () => Array.from(root.querySelectorAll('.site-accordion__content:not([inert])'));
	const onResize = () => openContents().forEach(refreshHeights);

	if (!root._accordionResizeBound) {
		window.addEventListener('resize', onResize, { passive: true });
		root._accordionResizeBound = true;
	}
}
export default initAccordions;
